/*
 * Copyright 2022 Oriflame
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { ScoreSuccessEnum } from '../api';

/**
 * Map a raw score percentage to a health level, using the same 50/80
 * thresholds as the score board gridlines (0/50/80/100). Returned value feeds
 * {@link scoreToColorConverter} so a bar/number can be tinted by health even
 * when the source data omits an explicit `scoreSuccess`.
 *
 * `null`/`undefined` (an unmeasured score) and any non-finite value
 * (`NaN`/`Infinity`/`-Infinity`) map to `undefined` — the converter then
 * renders it as the neutral "no data" grey, and callers report no-health
 * rather than a misleading pass/fail level.
 */
export function percentToScoreSuccess(
  percent: number | null | undefined,
): ScoreSuccessEnum | undefined {
  if (
    percent === null ||
    typeof percent === 'undefined' ||
    !Number.isFinite(percent)
  ) {
    return undefined;
  }
  if (percent >= 80) {
    return ScoreSuccessEnum.Success;
  }
  if (percent >= 50) {
    return ScoreSuccessEnum.Partial;
  }
  return ScoreSuccessEnum.Failure;
}
